import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';
import AllocateLicensesModal from './AllocateLicensesModal';
import * as api from '../../../api/index';

import {
  GetDOTLicenseBatchesCERT,
  GetDOTLicenseBatchDetailCERT,
} from '../../../redux/actions/certification/licenseBatchActions';

import global from "../../../global.scss";

const licenseStatusChipColor = (status) => {
  switch (status) {
    case 'AVAILABLE': return 'success';
    case 'ASSIGNED':  return 'info';
    case 'USED':      return 'default';
    default:          return 'default';
  }
};

const interviewStatusChipColor = (status) => {
  switch (status) {
    case 'NOT_STARTED': return 'default';
    case 'PENDING':     return 'info';
    case 'COMPLETED':   return 'success';
    case 'FAILED':      return 'error';
    default:            return 'default';
  }
};

const memberStatusChipColor = (status) => {
  switch (status) {
    case 'ACTIVE':  return 'success';
    case 'INVITED': return 'info';
    case 'REMOVED': return 'error';
    default:        return 'default';
  }
};

// ── Batch detail modal ────────────────────────────────────────────────────────
const BatchDetailModal = ({ open, setOpen, batch, dispatch }) => {
  const state = useSelector(s => s.certification);
  const [detailLoading, setDetailLoading] = useState(false);
  const detail = state.dotLicenseBatchDetail;
  const isThisBatch = detail?.batch?.id === batch?.id;

  const [expandedRows, setExpandedRows] = useState({});
  const [rowStats, setRowStats] = useState({});
  const [rowStatsLoading, setRowStatsLoading] = useState({});

  const toggleRow = async (licId, companyId) => {
    const isOpen = !!expandedRows[licId];
    setExpandedRows(prev => ({ ...prev, [licId]: !isOpen }));
    if (!isOpen && !rowStats[licId]) {
      setRowStatsLoading(prev => ({ ...prev, [licId]: true }));
      try {
        const { data } = await api.getDBECompanyStatsCERT(companyId);
        setRowStats(prev => ({ ...prev, [licId]: data.data }));
      } catch (e) {}
      setRowStatsLoading(prev => ({ ...prev, [licId]: false }));
    }
  };

  useEffect(() => {
    if (open && batch?.id) {
      setDetailLoading(true);
      setExpandedRows({});
      setRowStats({});
      dispatch(GetDOTLicenseBatchDetailCERT(batch.id, setDetailLoading));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => setOpen(false);

  const statCards = [
    { label: 'Total',     value: detail?.stats?.total,     color: 'rgba(80, 120, 200, 0.25)',  border: 'rgba(80, 120, 200, 0.5)' },
    { label: 'Available', value: detail?.stats?.available, color: 'rgba(60, 160, 100, 0.25)',  border: 'rgba(60, 160, 100, 0.5)' },
    { label: 'Assigned',  value: detail?.stats?.assigned,  color: 'rgba(60, 160, 200, 0.25)',  border: 'rgba(60, 160, 200, 0.5)' },
    { label: 'Used',      value: detail?.stats?.used,      color: 'rgba(200, 80,  80,  0.25)', border: 'rgba(200, 80,  80,  0.5)' },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="batch-detail-modal-title">
      <Box
        className="flexCenterCenterRow"
        width={"100vw"}
        height={"100vh"}
        sx={{ p: "20px" }}>
        <Box
          maxWidth={"xl"}
          width={"100%"}
          className="modal--wide flex_SBColumn"
          sx={{ maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {/* Header */}
          <Box sx={{ p: "20px 25px" }} className="flexCenterSBRow">
            <Box>
              <Typography id="batch-detail-modal-title" variant='h6' sx={{ color: global["primary"] }}>
                Batch Detail
              </Typography>
              {batch && (
                <Typography variant='caption' sx={{ color: "grey" }}>
                  {batch.authority_name} &nbsp;·&nbsp;
                  <span style={{ fontFamily: "monospace" }}>{batch.id}</span>
                </Typography>
              )}
            </Box>
            <Button variant='text' onClick={handleClose}>Close</Button>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box sx={{ p: "20px 25px", overflowY: "auto", flex: 1 }}>
            {
              detailLoading
              ?
              <Box className="flexCenterCenterRow" height={"200px"}>
                <CircularProgress />
              </Box>
              :
              isThisBatch && detail?.licenses
              ?
              <>
                {/* Stat cards */}
                <Box className="flexCenter_Row" gap={"12px"} mb={"24px"} flexWrap="wrap">
                  {statCards.map((card) => (
                    <Box
                      key={card.label}
                      sx={{
                        p: "12px 20px",
                        borderRadius: "10px",
                        backgroundColor: card.color,
                        border: `1px solid ${card.border}`,
                        minWidth: "110px",
                      }}>
                      <Typography variant='h5' fontWeight={700} color="white">
                        {card.value ?? "—"}
                      </Typography>
                      <Typography variant='caption' sx={{ color: "rgba(255,255,255,0.6)" }}>
                        {card.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Licenses table */}
                <Table className="block" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assigned To</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Timestamps</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.licenses.map((lic) => (
                      <React.Fragment key={lic.id}>
                      <TableRow key={lic.id} className="table__clickable-row">
                        <TableCell>
                          <Typography
                            variant='body2'
                            fontWeight={600}
                            sx={{ letterSpacing: "0.12em", fontFamily: "monospace" }}>
                            {lic.invite_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lic.status}
                            color={licenseStatusChipColor(lic.status)}
                            size="small"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          {lic.assigned_to_email ? (
                            <Box>
                              <Typography variant='body2'>{lic.assigned_to_email}</Typography>
                              <Typography variant='caption' sx={{ color: 'grey', fontFamily: 'monospace', fontSize: '10px' }}>
                                {lic.assigned_to_user_id ?? '—'}
                              </Typography>
                            </Box>
                          ) : <NA value={null} />}
                        </TableCell>
                        <TableCell>
                          {lic.company ? (
                            <Box>
                              <Typography variant='body2'>{lic.company.legal_name}</Typography>
                              <Typography variant='caption' sx={{ color: 'grey', fontFamily: 'monospace', fontSize: '10px' }}>
                                {lic.company_id}
                              </Typography>
                            </Box>
                          ) : <NA value={null} />}
                        </TableCell>
                        <TableCell>
                          <TableTimeStamps
                            createdAt={lic.created_at}
                            updatedAt={lic.updated_at}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {lic.company &&
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(lic.id, lic.company_id)}>
                              {expandedRows[lic.id]
                                ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: global['primary'] }} />
                                : <KeyboardArrowDownIcon fontSize="small" sx={{ color: global['primary'] }} />}
                            </IconButton>
                          }
                        </TableCell>
                      </TableRow>

                      {/* Collapsible members row */}
                      {lic.company && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedRows[lic.id] ? undefined : 'none' }}>
                            <Collapse in={!!expandedRows[lic.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ p: '12px 20px 16px 20px', backgroundColor: 'rgba(255,255,255,0.03)' }}>
                                <Typography variant='caption' fontWeight={600} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                  Owners &nbsp;·&nbsp; {lic.company.legal_name}
                                </Typography>
                                {rowStatsLoading[lic.id] ? (
                                  <Box sx={{ p: '16px 0' }}>
                                    <CircularProgress size={18} />
                                  </Box>
                                ) : rowStats[lic.id] ? (
                                  <Table size="small" className="block" sx={{ mt: '10px' }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Interview</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {rowStats[lic.id].users.map((user) => (
                                        <TableRow key={user.id} className="table__clickable-row">
                                          <TableCell sx={{ fontSize: '12px' }}>
                                            <Box className="flexCenter_Row" gap="6px">
                                              {user.email ?? '—'}
                                              {user.is_owner && (
                                                <Chip label="Owner" size="small" color="primary" sx={{ fontSize: '10px', height: '18px' }} />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={user.status}
                                              color={memberStatusChipColor(user.status)}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={user.interview_status}
                                              color={interviewStatusChipColor(user.interview_status)}
                                              size="small"
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : null}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </>
              :
              null
            }
          </Box>

        </Box>
      </Box>
    </Modal>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const LicenseBatchPageCertification = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.certification);
  const [loading, setLoading] = useState(true);
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [batchesList, setBatchesList] = useState([]);
  const [batchesTotal, setBatchesTotal] = useState(0);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Batch ID",     sort: "id" },
    { label: "Authority",    sort: "authority_name" },
    { label: "Authority ID", sort: "authority_id" },
    { label: "Total",        sort: "total_licenses" },
    { label: "Notes",        sort: null },
    { label: "Timestamps",   sort: "created_at" },
    { label: "",             sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetBatches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setBatchesList(state.dotLicenseBatches?.records || []);
      setBatchesTotal(state.dotLicenseBatches?.total || 0);
    } catch (err) {}
  }, [state]);

  const handleGetBatches = () => {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetDOTLicenseBatchesCERT(page + 1, rowsPerPage, query, setLoading));
  };

  return (
    <Box>

      <AllocateLicensesModal
        open={allocateModalOpen}
        setOpen={setAllocateModalOpen}
        handleGet={handleGetBatches}
      />

      <BatchDetailModal
        open={detailModalOpen}
        setOpen={setDetailModalOpen}
        batch={selectedBatch}
        dispatch={dispatch}
      />

      <TopBar
        title={"License Batches"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text="Allocate New Batch"
        button1OnClick={() => setAllocateModalOpen(true)}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        batchesList.length === 0
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <Typography variant='body2' sx={{ opacity: 0.5 }}>
            No license batches found. Create the first one.
          </Typography>
        </Box>
        :
        <Table className="block">
          <TableHead>
            <TableRow>
              {
                tableHeadingsList.map((heading, headingIndex) => (
                  heading.sort === null
                  ?
                  <TableCell key={headingIndex}>{heading.label}</TableCell>
                  :
                  <TableCell key={headingIndex}>
                    <TableSortLabel
                      active={sort === heading.sort ? true : false}
                      direction={sortDirection}
                      onClick={() => {
                        handleSort(heading.sort);
                      }}
                    >
                      {heading.label}
                    </TableSortLabel>
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              batchesList.map((batch) => (
                <TableRow key={batch.id} className="table__clickable-row">
                  <TableCell>
                    <Typography variant='body2' sx={{ fontFamily: "monospace", fontSize: "11px" }}>
                      {batch.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{batch.authority_name}</TableCell>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontFamily: "monospace", fontSize: "11px" }}>
                      {batch.authority_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{batch.total_licenses}</TableCell>
                  <TableCell><NA value={batch.notes} /></TableCell>
                  <TableCell>
                    <TableTimeStamps
                      createdAt={batch.created_at}
                      updatedAt={batch.updated_at}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedBatch(batch);
                        setDetailModalOpen(true);
                      }}>
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                sx={{ borderBottom: 0 }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                colSpan={7}
                count={batchesTotal}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page'
                  },
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      }

    </Box>
  );
};

export default LicenseBatchPageCertification;
